import Layout from "@/components/elements/Layout";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { getToolboxList, getToolboxTags } from "@/services/api/Toolbox";
import { MagnifyingGlass, Package, X } from "@phosphor-icons/react/dist/ssr";
import { format } from "date-fns";
import Image from "next/image";
import React, { useEffect, useState } from "react";

// Add this new component for pagination
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  return (
    <div className='flex items-center justify-center gap-2 mt-6'>
      <Button
        variant='outline'
        size='sm'
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        className='h-8 px-3'
      >
        Previous
      </Button>

      <div className='flex items-center gap-1'>
        {[...Array(totalPages)].map((_, idx) => {
          const pageNumber = idx + 1;
          // Show first page, last page, current page, and pages around current
          const shouldShow =
            pageNumber === 1 ||
            pageNumber === totalPages ||
            Math.abs(currentPage - pageNumber) <= 1;

          if (!shouldShow) {
            // Show dots only once between gaps
            if (
              pageNumber === currentPage - 2 ||
              pageNumber === currentPage + 2
            ) {
              return (
                <span key={`dot-${idx}`} className='text-white/40'>
                  ...
                </span>
              );
            }
            return null;
          }

          return (
            <Button
              key={pageNumber}
              variant={currentPage === pageNumber ? "default" : "outline"}
              size='sm'
              onClick={() => onPageChange(pageNumber)}
              className={`h-8 w-8 p-0 ${
                currentPage === pageNumber ? "" : "text-white/60"
              }`}
            >
              {pageNumber}
            </Button>
          );
        })}
      </div>

      <Button
        variant='outline'
        size='sm'
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        className='h-8 px-3'
      >
        Next
      </Button>
    </div>
  );
};

export default function Toolbox() {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [toolboxes, setToolboxes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedToolbox, setSelectedToolbox] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [availableTags, setAvailableTags] = useState([]);

  // Add this computed value
  const totalPages = Math.ceil(pagination.totalItems / pagination.perPage);

  // Function to fetch tags
  const fetchTags = async () => {
    try {
      const response = await getToolboxTags();
      setAvailableTags(response.data.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  // Function to fetch toolboxes - make it useCallback
  const fetchToolboxes = React.useCallback(
    async (resetPage = false) => {
      try {
        setLoading(true);
        const params = {
          is_active: 1,
          page: resetPage ? 1 : pagination.currentPage,
          per_page: pagination.perPage,
          name: searchQuery || undefined,
          tags: selectedTags.length ? selectedTags.join(",") : undefined,
        };

        const response = await getToolboxList({ params });
        setToolboxes(response.data.data);
        setPagination((prev) => ({
          ...prev,
          currentPage: resetPage ? 1 : prev.currentPage,
          totalItems: response.data.total,
        }));
      } catch (error) {
        console.error("Error fetching toolboxes:", error);
      } finally {
        setLoading(false);
      }
    },
    [pagination.currentPage, pagination.perPage, searchQuery, selectedTags]
  );

  // Update useEffect dependencies
  useEffect(() => {
    fetchToolboxes();
    fetchTags();
  }, [fetchToolboxes]); // Only depend on fetchToolboxes which is now memoized

  // Add handler for page changes
  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className='col-span-full flex justify-center items-center min-h-[200px]'>
          <div className='text-white/60'>Loading...</div>
        </div>
      );
    }

    if (!toolboxes.length) {
      return (
        <div className='col-span-full flex flex-col items-center justify-center min-h-[400px] text-center'>
          <Package className='w-16 h-16 mb-4 text-white/20' />
          <h3 className='mb-2 text-xl font-medium text-white'>
            No tools found
          </h3>
          <p className='max-w-md text-white/60'>
            {searchQuery
              ? `No tools match your search "${searchQuery}". Try different keywords or clear your search.`
              : "No tools are available at the moment. Please check back later."}
          </p>
          {searchQuery && (
            <Button
              variant='outline'
              className='mt-4'
              onClick={() => {
                setSearchQuery("");
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
            >
              Clear Search
            </Button>
          )}
        </div>
      );
    }

    return toolboxes.map((toolbox) => {
      // Convert tags string to array if it's a string
      const tagsList =
        typeof toolbox.tags === "string"
          ? toolbox.tags.split(",").map((tag) => tag.trim())
          : Array.isArray(toolbox.tags)
          ? toolbox.tags
          : [];

      return (
        <button
          type='button'
          onClick={() => {
            setSelectedToolbox(toolbox);
            setIsDetailOpen(true);
          }}
          key={toolbox.id}
          className='flex flex-col w-full overflow-hidden transition-colors rounded-xl bg-white/5 hover:bg-white/10'
        >
          <div className='w-full p-4 bg-white/5'>
            <Image
              src={toolbox.logo}
              alt={toolbox.name}
              width={100}
              height={100}
              className='object-contain w-full h-10'
            />
          </div>
          <div className='flex-1 p-4 pt-3 pb-0 text-left lg:pt-4 lg:p-5 lg:pb-0'>
            <p className='text-lg font-medium'>{toolbox.name}</p>

            <p className='mt-1 text-xs leading-snug opacity-30 line-clamp-3'>
              {toolbox.description}
            </p>
          </div>
          <div className='flex flex-wrap gap-1 p-4'>
            {tagsList.map((tag, index) => (
              <span
                key={index}
                className='text-[10px] text-white/60 font-medium border border-dashed rounded-full bg-white/5 border-white/10 px-2 py-0.5'
              >
                {tag}
              </span>
            ))}
          </div>
        </button>
      );
    });
  };

  return (
    <Layout title='Startup Toolbox' description=''>
      <div className='container min-h-screen my-10'>
        <div>
          <h1 className='mb-3 text-3xl font-bold tracking-tighter text-white lg:text-5xl'>
            Your Startup Toolbox Shop
          </h1>
          <p className='mt-2 text-sm lg:mt-4 lg:text-base opacity-60'>
            Browse our curated library of tools, and some of the best offers
            around to support your startup or VC in your next stage of growth.
          </p>
          <div className='mt-5 space-y-4'>
            <div className='bg-background/80 backdrop-blur-md'>
              <div className='relative flex w-full lg:mr-3 lg:flex-1'>
                <div className='absolute inset-y-0 left-0 flex items-center w-full pl-3 border-none pointer-events-none text-white/40 sm:text-sm '>
                  <MagnifyingGlass
                    className='w-4 h-4 text-white/40'
                    aria-hidden='true'
                  />
                </div>
                <form
                  className='w-full mr-2 lg:mr-0'
                  onSubmit={(e) => {
                    e.preventDefault();
                    fetchToolboxes(true); // Reset to page 1 when searching
                  }}
                >
                  <input
                    type='text'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder='Search tools...'
                    className='w-full p-3 text-sm text-white transition-colors duration-200 border-none rounded-md h-9 placeholder:text-white/40 pl-9 hover:bg-white/10 bg-white/5'
                  />
                </form>
              </div>
            </div>

            <div className='p-4 rounded-md bg-background/80 backdrop-blur-md'>
              <p className='mb-3 text-sm text-white/60'>Filter by Tags</p>
              <div className='flex flex-wrap gap-2'>
                {availableTags.map((tag) => (
                  <Button
                    key={tag.id}
                    variant={
                      selectedTags.includes(tag.title) ? "default" : "outline"
                    }
                    size='sm'
                    onClick={() => {
                      setSelectedTags((prev) => {
                        const newTags = prev.includes(tag.title)
                          ? prev.filter((t) => t !== tag.title)
                          : [...prev, tag.title];
                        return newTags;
                      });
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    className='h-8'
                  >
                    {tag.title}
                  </Button>
                ))}
              </div>

              {selectedTags.length > 0 && (
                <div className='flex flex-wrap items-center gap-2 mt-4'>
                  <span className='text-sm text-white/40'>Active filters:</span>
                  {selectedTags.map((tag) => (
                    <span
                      key={tag}
                      className='inline-flex items-center gap-1 text-xs text-white/60 font-medium border border-dashed rounded-full bg-white/5 border-white/10 px-2.5 py-1'
                    >
                      {tag}
                      <button
                        onClick={() => {
                          setSelectedTags((prev) =>
                            prev.filter((t) => t !== tag)
                          );
                          setPagination((prev) => ({
                            ...prev,
                            currentPage: 1,
                          }));
                        }}
                        className='text-white/40 hover:text-white'
                      >
                        <X className='w-3 h-3' />
                      </button>
                    </span>
                  ))}
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => {
                      setSelectedTags([]);
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    className='text-xs text-white/40 hover:text-white'
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className='mt-6 lg:mt-8'>
            <p className='pb-2 text-sm border-b lg:pb-3 border-white/10 text-white/40'>
              Showing{" "}
              <span className='font-medium text-white'>{toolboxes.length}</span>{" "}
              of{" "}
              <span className='font-medium text-white'>
                {pagination.totalItems}
              </span>{" "}
              Results
              {(searchQuery || selectedTags.length > 0) && (
                <span className='ml-1'>
                  {searchQuery && (
                    <span className='text-white/40'>
                      for &quot;{searchQuery}&quot;
                    </span>
                  )}
                  {selectedTags.length > 0 && (
                    <span className='text-white/40'>
                      {" "}
                      in {selectedTags.join(", ")}
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-4 mt-4 lg:mt-6 lg:grid-cols-5'>
            {renderContent()}
          </div>

          <Pagination
            currentPage={pagination.currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <Sheet
        open={isDetailOpen}
        onOpenChange={(open) => {
          setIsDetailOpen(open);
          if (!open) {
            setSelectedToolbox(null);
          }
        }}
      >
        <SheetContent className='flex flex-col min-w-[90%] p-0 lg:min-w-[400px] overflow-y-auto'>
          {selectedToolbox && (
            <>
              <div className='flex-1 p-5 space-y-6 lg:space-y-10 lg:p-7'>
                <div>
                  <Image
                    src={selectedToolbox.logo}
                    alt={selectedToolbox.name}
                    width={100}
                    height={100}
                    className='object-contain w-auto h-14 lg:h-16'
                  />
                  <p className='mt-4 text-xl font-medium lg:mt-6'>
                    {selectedToolbox.name}
                  </p>
                  <p className='mt-2 opacity-40'>
                    {selectedToolbox.description}
                  </p>
                </div>

                {/* <div>
                  <p className='mb-1 text-sm opacity-40'>Website</p>
                  <a
                    href={selectedToolbox.website}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='hover:underline'
                  >
                    {new URL(selectedToolbox.website).hostname}
                  </a>
                </div> */}

                <div>
                  <p className='mb-3 text-sm opacity-40'>Tags</p>
                  <div className='flex flex-wrap gap-1.5'>
                    {(typeof selectedToolbox.tags === "string"
                      ? selectedToolbox.tags.split(",").map((tag) => tag.trim())
                      : Array.isArray(selectedToolbox.tags)
                      ? selectedToolbox.tags
                      : []
                    ).map((tag, index) => (
                      <span
                        key={index}
                        className='text-xs text-white/60 font-medium border border-dashed rounded-full bg-white/5 border-white/10 px-2.5 py-1'
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedToolbox?.benefit && (
                  <div>
                    <p className='mb-1 text-sm lg:mb-2 opacity-40'>Benefit</p>
                    <p className=''>{selectedToolbox.benefit}</p>
                  </div>
                )}
                {selectedToolbox?.expired_on && (
                  <div>
                    <p className='mb-1 text-sm lg:mb-2 opacity-40'>
                      Expired on
                    </p>
                    <p className=''>
                      {format(
                        new Date(selectedToolbox.expired_on),
                        "MMMM d, yyyy"
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className='sticky bottom-0 w-full p-4 pt-2 lg:p-7 lg:pt-0 bg-background'>
                <p className='mb-2 text-xs leading-normal opacity-40'>
                  Unlock this exclusive offer by subscribing through our special
                  link. Don&apos;t miss out—click now to secure your deal!
                </p>
                <Button
                  className='w-full'
                  onClick={() => window.open(selectedToolbox.website, "_blank")}
                >
                  Get this Offer
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
